export const MAX_TRAINING_CERTIFICATE_BYTES = 20 * 1024 * 1024;

export function removeTrainingCertificateAt<T>(certificates: readonly T[], index: number): T[] {
  return certificates.filter((_, itemIndex) => itemIndex !== index);
}

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

const ACCEPTED_MIMES = new Set(Object.values(MIME_BY_EXTENSION));

export function trainingCertificateMime(file: File): string | null {
  const declaredMime = file.type.toLowerCase();
  if (ACCEPTED_MIMES.has(declaredMime)) return declaredMime;
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  return MIME_BY_EXTENSION[extension] ?? null;
}

export function trainingCertificateExtension(mime: string): string {
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/heic') return 'heic';
  if (mime === 'image/heif') return 'heif';
  throw new Error(`Unsupported training certificate MIME type: ${mime}`);
}

function hasPrefix(bytes: Uint8Array, prefix: number[]): boolean {
  return prefix.every((byte, index) => bytes[index] === byte);
}

function isHeif(bytes: Uint8Array): boolean {
  if (bytes.length < 12 || !hasPrefix(bytes, [0x00, 0x00, 0x00])) return false;
  const boxType = String.fromCharCode(...bytes.slice(4, 8));
  if (boxType !== 'ftyp') return false;
  const brand = String.fromCharCode(...bytes.slice(8, 12));
  return ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand);
}

export async function validateTrainingCertificateFile(file: File): Promise<string | null> {
  const mime = trainingCertificateMime(file);
  if (!mime) return 'Seleziona un PDF o una foto dell’attestato (JPG, PNG, HEIC o WebP).';
  if (file.size <= 0 || file.size > MAX_TRAINING_CERTIFICATE_BYTES) {
    return 'Il documento deve avere una dimensione massima di 20 MB.';
  }
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const valid = (mime === 'application/pdf' && hasPrefix(bytes, [0x25, 0x50, 0x44, 0x46, 0x2d]))
    || (mime === 'image/jpeg' && hasPrefix(bytes, [0xff, 0xd8, 0xff]))
    || (mime === 'image/png' && hasPrefix(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
    || (mime === 'image/webp' && String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF'
      && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP')
    || ((mime === 'image/heic' || mime === 'image/heif') && isHeif(bytes));
  if (!valid) return 'Il file selezionato non contiene un PDF o una foto valida.';
  return null;
}

/** @deprecated Usa validateTrainingCertificateFile per PDF e foto. */
export async function validateTrainingPdf(file: File): Promise<string | null> {
  if (trainingCertificateMime(file) !== 'application/pdf') {
    return 'Seleziona un file PDF.';
  }
  return validateTrainingCertificateFile(file);
}

export function formatTrainingFileSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('it-IT', {
    maximumFractionDigits: 1,
  })} MB`;
}
